import React, { useState } from 'react';
import { type PostIaServicesAskRequest } from '@europrocurement/l2d-domain/openApi/ApiIaService';
import { faSpellCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { useIaServicesService } from '@europrocurement/l2d-domain';
import { useSnackbar } from 'notistack';
import { type IaButtonProps } from '../../type/IaButtonProps';

const IaButton: React.FunctionComponent<IaButtonProps> = function ({
    applicationName = 'L2D_Correction',
    icon = faSpellCheck,
    label = "Correction d'orthographe",
    getParams,
    onSuccess,
    sxProps,
    sxLabelProps,
}) {
    const { palette } = useTheme();
    const { iaServicesApi } = useIaServicesService();
    const { enqueueSnackbar } = useSnackbar();

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const handleButton = async () => {
        setIsSubmitting(true);

        const params = getParams();

        const request: PostIaServicesAskRequest = {
            application: applicationName,
            params,
        };

        const response = await iaServicesApi.apiIaServiceAsk(request);

        if ([200, 201].includes(response.status)) {
            onSuccess(response.data);
        } else {
            enqueueSnackbar('Une erreur est survenue lors du traitement', {
                variant: 'error',
            });
        }

        setIsSubmitting(false);
    };

    return (
        <Button
            onClick={handleButton}
            disabled={isSubmitting}
            variant="contained"
            key="ask-ia-btn"
            sx={{
                padding: '8px 20px',
                fontSize: '16px',
                gap: '8px',
                background: palette.primary.dark,
                ':hover': {
                    background: palette.primary.main,
                },
                transition: 'all ease-in-out 300ms',
                ...sxProps,
            }}
        >
            <FontAwesomeIcon
                icon={icon}
                data-testid="ia-button-icon"
            />
            {isSubmitting ? (
                <CircularProgress
                    color="inherit"
                    size="16px"
                />
            ) : (
                <Typography
                    variant="l2dcaption"
                    sx={sxLabelProps}
                >
                    {label}
                </Typography>
            )}
        </Button>
    );
};

export default IaButton;
